import clsx from 'clsx';
import { useDashboardStore, type SortBy, type SortOrder } from '@store/dashboardStore';
import { useCameraGroups } from '@hooks/useCameraGroups';
import type {
  CameraStatus,
  CameraType,
  RecordingMode,
  StorageTarget,
} from '@api/types';

const SELECT_CLASSES = clsx(
  'h-9 rounded-md border border-border bg-surface px-2 pr-8 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
);

export function FilterBar(): JSX.Element {
  const { filters, setFilter } = useDashboardStore();
  const { groups, isLoading: groupsLoading, error: groupsError } = useCameraGroups();

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border bg-surface/40 px-6 py-3 text-sm">
      <Select
        label="Status"
        value={filters.status ?? ''}
        onChange={(v) => setFilter('status', (v as CameraStatus) || undefined)}
        options={[
          { value: '', label: 'All statuses' },
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ]}
      />
      <Select
        label="Type"
        value={filters.type ?? ''}
        onChange={(v) => setFilter('type', (v as CameraType) || undefined)}
        options={[
          { value: '', label: 'All types' },
          { value: 'standard', label: 'Standard' },
          { value: 'ptz', label: 'PTZ' },
          { value: '360', label: '360°' },
        ]}
      />
      <Select
        label="Mode"
        value={filters.recordingMode ?? ''}
        onChange={(v) => setFilter('recordingMode', (v as RecordingMode) || undefined)}
        options={[
          { value: '', label: 'All modes' },
          { value: 'continuous', label: 'Continuous' },
          { value: 'scheduled', label: 'Scheduled' },
          { value: 'manual', label: 'Manual' },
        ]}
      />
      <Select
        label="Storage"
        value={filters.storageTarget ?? ''}
        onChange={(v) => setFilter('storageTarget', (v as StorageTarget) || undefined)}
        options={[
          { value: '', label: 'All storage' },
          // { value: 'nas', label: 'NAS' },
          { value: 'local', label: 'Local' },
        ]}
      />
      <Select
        label="Group"
        value={filters.groupId !== undefined ? String(filters.groupId) : ''}
        disabled={groupsLoading || Boolean(groupsError)}
        onChange={(v) => setFilter('groupId', v ? Number(v) : undefined)}
        options={[
          { value: '', label: groupsError ? 'Groups unavailable' : 'All groups' },
          ...groups.map((g) => ({ value: String(g.id), label: g.name })),
        ]}
      />

      <div className="ml-auto flex items-center gap-2">
        <Select
          label="Sort"
          value={filters.sortBy}
          onChange={(v) => setFilter('sortBy', v as SortBy)}
          options={[
            { value: 'name', label: 'Name' },
            { value: 'createdAt', label: 'Created' },
            { value: 'status', label: 'Status' },
          ]}
        />
        <Select
          label="Order"
          value={filters.sortOrder}
          onChange={(v) => setFilter('sortOrder', v as SortOrder)}
          options={[
            { value: 'asc', label: '↑ Asc' },
            { value: 'desc', label: '↓ Desc' },
          ]}
        />
      </div>
    </div>
  );
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}

function Select({ label, value, onChange, options, disabled }: SelectProps): JSX.Element {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-muted">
      <span className="select-none">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={SELECT_CLASSES}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
